/**
 * DEMO MODE SERVICE
 *
 * Serviço de modo demonstração para usuários sem assinatura ativa.
 * Permite visualizar o app com dados mock completos de uma agência fictícia com 5 clientes.
 *
 * Funcionalidades:
 * - Toggle no header para ativar/desativar modo demo
 * - Dados completos para todas as telas (dashboard, clientes, perfil)
 * - Métricas realistas de Meta Ads e Google Ads
 * - Dados demográficos, geográficos e de campanhas
 */

import { formatDateISO, generateDateRange } from '../utils/date-utils.js';
import { localeService } from '../i18n/index.js';
import { regionService } from './region.js';

class DemoModeService {
  constructor() {
    this.isEnabled = false;
    this.LOCAL_STORAGE_KEY = 'summdash_demo_mode';
    this._listeners = [];

    // Inicializa estado a partir do localStorage
    this._init();
  }

  _init() {
    const saved = localStorage.getItem(this.LOCAL_STORAGE_KEY);
    if (saved === 'true') {
      // Marca como pendente - será validado quando user data estiver disponível
      this._pendingRestore = true;
      this.isEnabled = true;
      document.documentElement.setAttribute('data-demo-mode', 'true');
    }
  }

  /**
   * Valida se demo mode deve permanecer ativo.
   * Chamado quando user data fica disponível.
   * Se o usuário tem assinatura ativa, desativa o demo mode.
   */
  validateAccess(hasSubscription) {
    if (hasSubscription && this.isEnabled) {
      this.disable();
    }
    this._pendingRestore = false;
  }

  /**
   * Ativa o modo demo
   */
  enable() {
    this.isEnabled = true;
    localStorage.setItem(this.LOCAL_STORAGE_KEY, 'true');
    document.documentElement.setAttribute('data-demo-mode', 'true');
    this._notifyListeners();
    return true;
  }

  /**
   * Desativa o modo demo
   */
  disable() {
    this.isEnabled = false;
    localStorage.setItem(this.LOCAL_STORAGE_KEY, 'false');
    document.documentElement.removeAttribute('data-demo-mode');
    this._notifyListeners();
    return false;
  }

  /**
   * Toggle do modo demo
   */
  toggle() {
    if (this.isEnabled) {
      return this.disable();
    } else {
      return this.enable();
    }
  }

  /**
   * Verifica se o modo demo está ativo
   */
  getStatus() {
    return this.isEnabled;
  }

  /**
   * Registra listener para mudanças no modo demo
   */
  onStatusChange(callback) {
    this._listeners.push(callback);
    return () => {
      this._listeners = this._listeners.filter(cb => cb !== callback);
    };
  }

  _notifyListeners() {
    this._listeners.forEach(cb => cb(this.isEnabled));
  }

  // ==================== DADOS MOCK DA AGÊNCIA ====================

  /**
   * Retorna dados da agência demo
   */
  getDemoAgency() {
    return {
      id: 'demo_agency_001',
      name: 'Agência Demo Digital',
      email: 'contato@agenciademo.com.br',
      phone: '(11) 99999-8888',
      website: 'https://agenciademo.com.br',
      address: {
        street: 'Av. Paulista, 1000',
        city: 'São Paulo',
        state: 'SP',
        zipCode: '01310-100',
        country: 'Brasil'
      },
      primaryColor: '#6366f1',
      secondaryColor: '#8b5cf6',
      headingFont: 'Inter',
      bodyFont: 'Inter',
      logoUrl: null,
      ownerId: 'demo_user_001',
      createdAt: '2024-01-15T10:00:00.000Z',
      subscription: {
        plan: 'professional',
        maxClients: 15,
        status: 'active'
      },
      settings: {
        metaOAuthConnected: true,
        googleOAuthConnected: true,
        metaBusinessName: 'Demo Business Manager',
        googleAccountEmail: 'demo@agenciademo.com.br'
      }
    };
  }

  // ==================== DADOS MOCK DOS CLIENTES ====================

  /**
   * Retorna lista de 5 clientes demo com dados completos
   */
  getDemoClients() {
    return [
      {
        id: 'demo_client_001',
        name: 'TechStore Brasil',
        email: 'marketing@techstore.com.br',
        phone: '(11) 3333-4444',
        company: 'TechStore Comércio LTDA',
        industry: 'E-commerce / Tecnologia',
        website: 'https://techstore.com.br',
        status: 'active',
        photoUrl: null,
        startDate: '2024-02-01',
        contractStartDate: '2024-02-01', // Para gráfico de clientes ativos
        endDate: null,
        metaAdAccountIds: ['act_demo_001'],
        googleAdAccountIds: ['123-456-7890'],
        objectives: ['sales', 'leads'],
        monthlyBudget: 25000,
        notes: 'Cliente focado em vendas de eletrônicos. Campanhas de remarketing performam muito bem.',
        accessPermissions: {
          canViewReports: true,
          canEditProfile: false,
          canExportData: true
        },
        kommo: { connected: true, pipelineId: 'demo_pipe_001', pipelineName: 'Pipeline Vendas' },
        createdAt: '2024-02-01T10:00:00.000Z'
      },
      {
        id: 'demo_client_002',
        name: 'Bella Moda Fashion',
        email: 'contato@bellamoda.com.br',
        phone: '(21) 2222-3333',
        company: 'Bella Moda Confecções S.A.',
        industry: 'Moda / Vestuário',
        website: 'https://bellamoda.com.br',
        status: 'active',
        photoUrl: null,
        startDate: '2024-03-15',
        contractStartDate: '2024-03-15', // Para gráfico de clientes ativos
        endDate: null,
        metaAdAccountIds: ['act_demo_002'],
        googleAdAccountIds: ['234-567-8901'],
        objectives: ['awareness', 'engagement', 'sales'],
        monthlyBudget: 18000,
        notes: 'Marca de moda feminina premium. Forte presença no Instagram.',
        accessPermissions: {
          canViewReports: true,
          canEditProfile: true,
          canExportData: true
        },
        createdAt: '2024-03-15T14:30:00.000Z'
      },
      {
        id: 'demo_client_003',
        name: 'FitLife Academia',
        email: 'marketing@fitlife.com.br',
        phone: '(31) 4444-5555',
        company: 'FitLife Academias LTDA',
        industry: 'Fitness / Saúde',
        website: 'https://fitlife.com.br',
        status: 'active',
        photoUrl: null,
        startDate: '2024-01-20',
        contractStartDate: '2024-01-20', // Para gráfico de clientes ativos
        endDate: null,
        metaAdAccountIds: ['act_demo_003'],
        googleAdAccountIds: ['345-678-9012'],
        objectives: ['leads', 'local'],
        monthlyBudget: 12000,
        notes: 'Rede de academias com 5 unidades. Foco em captação de leads locais.',
        accessPermissions: {
          canViewReports: true,
          canEditProfile: false,
          canExportData: false
        },
        createdAt: '2024-01-20T09:00:00.000Z'
      },
      {
        id: 'demo_client_004',
        name: 'Sabor & Arte Restaurante',
        email: 'reservas@saborarte.com.br',
        phone: '(41) 5555-6666',
        company: 'Sabor & Arte Gastronomia LTDA',
        industry: 'Gastronomia / Restaurante',
        website: 'https://saborarte.com.br',
        status: 'active',
        photoUrl: null,
        startDate: '2024-04-01',
        contractStartDate: '2024-04-01', // Para gráfico de clientes ativos
        endDate: null,
        metaAdAccountIds: ['act_demo_004'],
        googleAdAccountIds: [],
        objectives: ['awareness', 'messages', 'local'],
        monthlyBudget: 8000,
        notes: 'Restaurante fine dining. Campanhas focadas em reservas via WhatsApp.',
        accessPermissions: {
          canViewReports: true,
          canEditProfile: false,
          canExportData: true
        },
        createdAt: '2024-04-01T11:00:00.000Z'
      },
      {
        id: 'demo_client_005',
        name: 'EduTech Cursos Online',
        email: 'marketing@edutech.com.br',
        phone: '(51) 6666-7777',
        company: 'EduTech Educação Digital S.A.',
        industry: 'Educação / E-learning',
        website: 'https://edutech.com.br',
        status: 'active',
        photoUrl: null,
        startDate: '2024-02-20',
        contractStartDate: '2024-02-20', // Para gráfico de clientes ativos
        endDate: null,
        metaAdAccountIds: ['act_demo_005'],
        googleAdAccountIds: ['456-789-0123'],
        objectives: ['leads', 'sales', 'awareness'],
        monthlyBudget: 35000,
        notes: 'Plataforma de cursos online. Alto volume de leads e conversões.',
        accessPermissions: {
          canViewReports: true,
          canEditProfile: true,
          canExportData: true
        },
        kommo: { connected: true, pipelineId: 'demo_pipe_005', pipelineName: 'Pipeline Cursos' },
        createdAt: '2024-02-20T16:00:00.000Z'
      }
    ];
  }

  /**
   * Retorna um cliente demo específico por ID
   */
  getDemoClient(clientId) {
    return this.getDemoClients().find(c => c.id === clientId) || null;
  }

  // ==================== MÉTRICAS DIÁRIAS MOCK ====================

  /**
   * Gera métricas diárias para um cliente no período especificado
   */
  generateDailyMetrics(clientId, startDate, endDate) {
    const dates = generateDateRange(startDate, endDate);
    const clientConfig = this._getClientMetricsConfig(clientId);
    const metrics = {};

    dates.forEach((dateStr, index) => {
      // Variação baseada no dia da semana
      const date = new Date(dateStr);
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      // Tendência de crescimento ao longo do período
      const growthFactor = 1 + (index / dates.length) * 0.15;

      // Variances INDEPENDENTES por tipo de métrica (simula flutuação real)
      const spendVar = (isWeekend ? 0.85 : 1.05) * (0.80 + Math.random() * 0.40);    // ±20%
      const impVar = (isWeekend ? 0.75 : 1.0) * (0.70 + Math.random() * 0.60);       // ±30%
      const clickVar = (isWeekend ? 0.80 : 1.0) * (0.75 + Math.random() * 0.50);     // ±25%
      const leadsVar = (isWeekend ? 1.10 : 1.0) * (0.55 + Math.random() * 0.90);     // ±45%
      const msgsVar = (isWeekend ? 1.15 : 1.0) * (0.60 + Math.random() * 0.80);      // ±40%
      const salesVar = (isWeekend ? 1.10 : 1.0) * (0.50 + Math.random() * 1.00);     // ±50%
      const revenueVar = (isWeekend ? 1.05 : 1.0) * (0.45 + Math.random() * 1.10);   // ±55%
      const reachVar = (isWeekend ? 0.80 : 1.0) * (0.70 + Math.random() * 0.60);     // ±30%

      // Extrair valores de mensagens (suporte a formato antigo e novo)
      const metaMessages = typeof clientConfig.dailyMessages === 'object'
        ? clientConfig.dailyMessages.meta
        : clientConfig.dailyMessages || 0;
      const googleMessages = typeof clientConfig.dailyMessages === 'object'
        ? clientConfig.dailyMessages.google || 0
        : 0;

      // Extrair valores de vendas
      const metaSales = clientConfig.dailySales?.meta || Math.round(clientConfig.dailyConversions.meta * 0.3);
      const googleSales = clientConfig.dailySales?.google || Math.round(clientConfig.dailyConversions.google * 0.4);

      const metaSpend = Math.round(clientConfig.dailySpend.meta * spendVar * growthFactor * 100) / 100;
      const metaClicks = Math.round(clientConfig.dailyClicks.meta * clickVar * growthFactor);

      metrics[dateStr] = {
        // Meta Ads
        meta: {
          spend: metaSpend,
          impressions: Math.round(clientConfig.dailyImpressions.meta * impVar * growthFactor),
          reach: Math.round(clientConfig.dailyReach.meta * reachVar * growthFactor),
          clicks: metaClicks,
          ctr: (Math.random() * 2 + 1.5).toFixed(2),
          cpc: metaClicks > 0 ? (metaSpend / metaClicks).toFixed(2) : '0.00',
          frequency: (1 + Math.random() * 0.8).toFixed(2),
          conversions: Math.round(clientConfig.dailyConversions.meta * salesVar * growthFactor),
          conversionValue: Math.round(clientConfig.dailyRevenue.meta * revenueVar * growthFactor * 100) / 100,
          leads: Math.max(0, Math.round(clientConfig.dailyLeads.meta * leadsVar * growthFactor)),
          messages: Math.max(0, Math.round(metaMessages * msgsVar * growthFactor)),
          sales: Math.max(0, Math.round(metaSales * salesVar * growthFactor)),
          profileVisits: Math.round((clientConfig.dailyProfileVisits || 0) * impVar * growthFactor)
        },
        // Google Ads
        google: clientConfig.hasGoogle ? (() => {
          const gSpend = Math.round(clientConfig.dailySpend.google * spendVar * growthFactor * 100) / 100;
          const gClicks = Math.round(clientConfig.dailyClicks.google * clickVar * growthFactor);
          return {
            spend: gSpend,
            impressions: Math.round(clientConfig.dailyImpressions.google * impVar * growthFactor),
            reach: Math.round(clientConfig.dailyReach.google * reachVar * growthFactor),
            clicks: gClicks,
            ctr: (Math.random() * 3 + 2).toFixed(2),
            cpc: gClicks > 0 ? (gSpend / gClicks).toFixed(2) : '0.00',
            conversions: Math.round(clientConfig.dailyConversions.google * salesVar * growthFactor),
            conversionValue: Math.round(clientConfig.dailyRevenue.google * revenueVar * growthFactor * 100) / 100,
            leads: Math.max(0, Math.round(clientConfig.dailyLeads.google * leadsVar * growthFactor)),
            messages: Math.max(0, Math.round(googleMessages * msgsVar * growthFactor)),
            sales: Math.max(0, Math.round(googleSales * salesVar * growthFactor))
          };
        })() : null,
        // Combinado
        combined: {
          spend: 0,
          impressions: 0,
          clicks: 0,
          conversions: 0,
          conversionValue: 0,
          leads: 0
        }
      };

      // Calcular combinado
      const m = metrics[dateStr];
      m.combined.spend = m.meta.spend + (m.google?.spend || 0);
      m.combined.impressions = m.meta.impressions + (m.google?.impressions || 0);
      m.combined.clicks = m.meta.clicks + (m.google?.clicks || 0);
      m.combined.conversions = m.meta.conversions + (m.google?.conversions || 0);
      m.combined.conversionValue = m.meta.conversionValue + (m.google?.conversionValue || 0);
      m.combined.leads = m.meta.leads;

      // Adicionar conversionsByType para os gráficos de leads/mensagens
      m.conversionsByType = {};

      // ========== META ADS ==========
      // Leads do Meta
      if (m.meta.leads > 0) {
        m.conversionsByType['lead'] = {
          count: m.meta.leads,
          value: 0,
          platform: 'meta'
        };
      }

      // Mensagens do Meta
      if (m.meta.messages > 0) {
        m.conversionsByType['onsite_conversion.messaging_conversation_started_7d'] = {
          count: m.meta.messages,
          value: 0,
          platform: 'meta'
        };
      }

      // Vendas/Purchases do Meta (usar valor de sales calculado)
      // IMPORTANTE: usar 'purchase' (não 'omni_purchase' que é KNOWN_DUPLICATE_TYPES)
      if (m.meta.sales > 0) {
        m.conversionsByType['purchase'] = {
          count: m.meta.sales,
          value: m.meta.conversionValue || 0,
          platform: 'meta'
        };
      }

      // Visitas ao perfil do Instagram
      if (m.meta.profileVisits > 0) {
        m.conversionsByType['onsite_conversion.ig_profile_visit'] = {
          count: m.meta.profileVisits,
          value: 0,
          platform: 'meta'
        };
      }

      // ========== GOOGLE ADS ==========
      if (clientConfig.hasGoogle && m.google) {
        // Leads do Google - usar valores já calculados
        if (m.google.leads > 0) {
          // Distribuir entre diferentes tipos de lead do Google
          const submitLeadForm = Math.round(m.google.leads * 0.6); // 60% formulários
          const qualifiedLead = m.google.leads - submitLeadForm; // 40% leads qualificados

          if (submitLeadForm > 0) {
            m.conversionsByType['google_submit_lead_form'] = {
              count: submitLeadForm,
              value: 0,
              platform: 'google'
            };
          }
          if (qualifiedLead > 0) {
            m.conversionsByType['google_qualified_lead'] = {
              count: qualifiedLead,
              value: 0,
              platform: 'google'
            };
          }
        }

        // Mensagens/Contatos do Google - usar valor já calculado
        if (m.google.messages > 0) {
          m.conversionsByType['google_contact_message'] = {
            count: m.google.messages,
            value: 0,
            platform: 'google'
          };
        }

        // Vendas/Purchases do Google - usar valor de sales calculado
        if (m.google.sales > 0) {
          m.conversionsByType['google_purchase'] = {
            count: m.google.sales,
            value: m.google.conversionValue || 0,
            platform: 'google'
          };
        }
      }
    });

    return metrics;
  }

  /**
   * Configuração de métricas por cliente (base para geração)
   */
  _getClientMetricsConfig(clientId) {
    const configs = {
      'demo_client_001': { // TechStore - E-commerce alto volume
        dailySpend: { meta: 600, google: 250 },
        dailyImpressions: { meta: 80000, google: 25000 },
        dailyReach: { meta: 45000, google: 18000 }, // Google Display/YouTube alcança usuários únicos
        dailyClicks: { meta: 1800, google: 800 },
        dailyConversions: { meta: 45, google: 25 },
        dailyRevenue: { meta: 8500, google: 4200 },
        dailyLeads: { meta: 15, google: 8 },
        dailyMessages: { meta: 20, google: 6 }, // Mensagens também do Google (click-to-message)
        dailySales: { meta: 14, google: 10 }, // Vendas separadas por plataforma
        dailyProfileVisits: 350,
        hasGoogle: true
      },
      'demo_client_002': { // Bella Moda - Fashion
        dailySpend: { meta: 450, google: 150 },
        dailyImpressions: { meta: 95000, google: 18000 },
        dailyReach: { meta: 55000, google: 12000 },
        dailyClicks: { meta: 2200, google: 450 },
        dailyConversions: { meta: 28, google: 12 },
        dailyRevenue: { meta: 4200, google: 1800 },
        dailyLeads: { meta: 8, google: 3 },
        dailyMessages: { meta: 45, google: 8 },
        dailySales: { meta: 8, google: 5 },
        dailyProfileVisits: 580,
        hasGoogle: true
      },
      'demo_client_003': { // FitLife - Academia local
        dailySpend: { meta: 300, google: 100 },
        dailyImpressions: { meta: 35000, google: 12000 },
        dailyReach: { meta: 22000, google: 8000 },
        dailyClicks: { meta: 650, google: 280 },
        dailyConversions: { meta: 8, google: 4 },
        dailyRevenue: { meta: 1200, google: 600 },
        dailyLeads: { meta: 25, google: 10 },
        dailyMessages: { meta: 35, google: 12 },
        dailySales: { meta: 2, google: 2 },
        dailyProfileVisits: 180,
        hasGoogle: true
      },
      'demo_client_004': { // Sabor & Arte - Restaurante (só Meta)
        dailySpend: { meta: 200, google: 0 },
        dailyImpressions: { meta: 28000, google: 0 },
        dailyReach: { meta: 18000, google: 0 },
        dailyClicks: { meta: 420, google: 0 },
        dailyConversions: { meta: 5, google: 0 },
        dailyRevenue: { meta: 800, google: 0 },
        dailyLeads: { meta: 3, google: 0 },
        dailyMessages: { meta: 65, google: 0 },
        dailySales: { meta: 2, google: 0 },
        dailyProfileVisits: 220,
        hasGoogle: false
      },
      'demo_client_005': { // EduTech - Cursos online
        dailySpend: { meta: 850, google: 350 },
        dailyImpressions: { meta: 120000, google: 35000 },
        dailyReach: { meta: 75000, google: 25000 },
        dailyClicks: { meta: 3500, google: 1100 },
        dailyConversions: { meta: 65, google: 28 },
        dailyRevenue: { meta: 12000, google: 5200 },
        dailyLeads: { meta: 85, google: 35 },
        dailyMessages: { meta: 40, google: 15 },
        dailySales: { meta: 20, google: 11 },
        dailyProfileVisits: 450,
        hasGoogle: true
      }
    };

    return configs[clientId] || configs['demo_client_001'];
  }

  // ==================== DADOS DE CAMPANHAS MOCK ====================

  /**
   * Retorna campanhas mock para um cliente
   */
  getDemoCampaigns(clientId) {
    const campaignTemplates = {
      'demo_client_001': [
        {
          id: 'camp_001_1',
          name: 'TechStore - Remarketing Carrinho',
          status: 'ACTIVE',
          objective: 'CONVERSIONS',
          dailyBudget: 150,
          spend: 4200,
          impressions: 185000,
          clicks: 4800,
          conversions: 142,
          conversionValue: 28500,
          ctr: 2.59,
          roas: 6.79
        },
        {
          id: 'camp_001_2',
          name: 'TechStore - Prospecção Lookalike',
          status: 'ACTIVE',
          objective: 'CONVERSIONS',
          dailyBudget: 200,
          spend: 5600,
          impressions: 320000,
          clicks: 6200,
          conversions: 98,
          conversionValue: 19600,
          ctr: 1.94,
          roas: 3.50
        },
        {
          id: 'camp_001_3',
          name: 'TechStore - Black Friday 2024',
          status: 'PAUSED',
          objective: 'CONVERSIONS',
          dailyBudget: 500,
          spend: 12000,
          impressions: 850000,
          clicks: 18500,
          conversions: 385,
          conversionValue: 115500,
          ctr: 2.18,
          roas: 9.63
        }
      ],
      'demo_client_002': [
        {
          id: 'camp_002_1',
          name: 'Bella Moda - Coleção Verão',
          status: 'ACTIVE',
          objective: 'CONVERSIONS',
          dailyBudget: 180,
          spend: 5040,
          impressions: 420000,
          clicks: 8400,
          conversions: 126,
          conversionValue: 18900,
          ctr: 2.00,
          roas: 3.75
        },
        {
          id: 'camp_002_2',
          name: 'Bella Moda - Instagram Engajamento',
          status: 'ACTIVE',
          objective: 'ENGAGEMENT',
          dailyBudget: 100,
          spend: 2800,
          impressions: 580000,
          clicks: 12500,
          conversions: 0,
          conversionValue: 0,
          ctr: 2.16,
          roas: 0
        }
      ],
      'demo_client_003': [
        {
          id: 'camp_003_1',
          name: 'FitLife - Captação Leads Janeiro',
          status: 'ACTIVE',
          objective: 'LEAD_GENERATION',
          dailyBudget: 120,
          spend: 3360,
          impressions: 145000,
          clicks: 2900,
          conversions: 245,
          conversionValue: 0,
          ctr: 2.00,
          roas: 0,
          leads: 245,
          costPerLead: 13.71
        }
      ],
      'demo_client_004': [
        {
          id: 'camp_004_1',
          name: 'Sabor Arte - Reservas WhatsApp',
          status: 'ACTIVE',
          objective: 'MESSAGES',
          dailyBudget: 80,
          spend: 2240,
          impressions: 98000,
          clicks: 1850,
          conversions: 420,
          conversionValue: 0,
          ctr: 1.89,
          roas: 0,
          messages: 420,
          costPerMessage: 5.33
        }
      ],
      'demo_client_005': [
        {
          id: 'camp_005_1',
          name: 'EduTech - Curso Python Full',
          status: 'ACTIVE',
          objective: 'CONVERSIONS',
          dailyBudget: 350,
          spend: 9800,
          impressions: 520000,
          clicks: 15600,
          conversions: 285,
          conversionValue: 142500,
          ctr: 3.00,
          roas: 14.54
        },
        {
          id: 'camp_005_2',
          name: 'EduTech - Leads Gratuito',
          status: 'ACTIVE',
          objective: 'LEAD_GENERATION',
          dailyBudget: 200,
          spend: 5600,
          impressions: 380000,
          clicks: 9500,
          conversions: 1850,
          conversionValue: 0,
          ctr: 2.50,
          roas: 0,
          leads: 1850,
          costPerLead: 3.03
        }
      ]
    };

    return campaignTemplates[clientId] || [];
  }

  /**
   * Retorna campanhas Google Ads demo para um cliente
   */
  getDemoGoogleCampaigns(clientId) {
    const config = this._getClientMetricsConfig(clientId);
    if (!config.hasGoogle) return [];

    const googleCampaignTemplates = {
      'demo_client_001': [ // TechStore - E-commerce
        {
          id: 'gcamp_001_1',
          name: 'TechStore - Search Produtos',
          status: 'ACTIVE',
          objective: 'SALES',
          campaignType: 'SEARCH',
          dailyBudget: 150,
          spend: 4200,
          impressions: 125000,
          clicks: 5800,
          conversions: 185,
          conversionValue: 32500,
          ctr: 4.64,
          roas: 7.74
        },
        {
          id: 'gcamp_001_2',
          name: 'TechStore - Shopping',
          status: 'ACTIVE',
          objective: 'SALES',
          campaignType: 'SHOPPING',
          dailyBudget: 100,
          spend: 2800,
          impressions: 85000,
          clicks: 3200,
          conversions: 95,
          conversionValue: 18500,
          ctr: 3.76,
          roas: 6.61
        }
      ],
      'demo_client_002': [ // Bella Moda
        {
          id: 'gcamp_002_1',
          name: 'Bella Moda - Shopping Roupas',
          status: 'ACTIVE',
          objective: 'SALES',
          campaignType: 'SHOPPING',
          dailyBudget: 80,
          spend: 2240,
          impressions: 72000,
          clicks: 1850,
          conversions: 68,
          conversionValue: 8500,
          ctr: 2.57,
          roas: 3.79
        }
      ],
      'demo_client_003': [ // FitLife - Academia
        {
          id: 'gcamp_003_1',
          name: 'FitLife - Search Local',
          status: 'ACTIVE',
          objective: 'LEADS',
          campaignType: 'SEARCH',
          dailyBudget: 60,
          spend: 1680,
          impressions: 42000,
          clicks: 1850,
          conversions: 125,
          conversionValue: 0,
          ctr: 4.40,
          roas: 0,
          leads: 125,
          costPerLead: 13.44
        }
      ],
      'demo_client_005': [ // EduTech - Cursos
        {
          id: 'gcamp_005_1',
          name: 'EduTech - Search Cursos',
          status: 'ACTIVE',
          objective: 'SALES',
          campaignType: 'SEARCH',
          dailyBudget: 200,
          spend: 5600,
          impressions: 165000,
          clicks: 6800,
          conversions: 245,
          conversionValue: 48500,
          ctr: 4.12,
          roas: 8.66
        },
        {
          id: 'gcamp_005_2',
          name: 'EduTech - YouTube Discovery',
          status: 'ACTIVE',
          objective: 'LEADS',
          campaignType: 'VIDEO',
          dailyBudget: 150,
          spend: 4200,
          impressions: 350000,
          clicks: 8500,
          conversions: 580,
          conversionValue: 0,
          ctr: 2.43,
          roas: 0,
          leads: 580,
          costPerLead: 7.24
        }
      ]
    };

    return googleCampaignTemplates[clientId] || [];
  }

  // ==================== DADOS DEMOGRÁFICOS MOCK ====================

  /**
   * Retorna dados demográficos mock no formato esperado pela página de perfil
   * Formato: { genderAge: [], device: [], region: [] }
   */
  getDemoDemographics(clientId) {
    // Configurações base por tipo de cliente
    const demographicProfiles = {
      'demo_client_001': { // Tech - mais masculino, jovens adultos
        gender: { male: 62, female: 35, unknown: 3 },
        age: { '18-24': 22, '25-34': 38, '35-44': 25, '45-54': 10, '55-64': 4, '65+': 1 },
        device: { mobile_app: 45, mobile_web: 23, desktop: 28, tablet: 4 }
      },
      'demo_client_002': { // Moda - mais feminino
        gender: { male: 18, female: 78, unknown: 4 },
        age: { '18-24': 28, '25-34': 42, '35-44': 18, '45-54': 8, '55-64': 3, '65+': 1 },
        device: { mobile_app: 55, mobile_web: 27, desktop: 14, tablet: 4 }
      },
      'demo_client_003': { // Academia - equilibrado
        gender: { male: 48, female: 50, unknown: 2 },
        age: { '18-24': 32, '25-34': 35, '35-44': 20, '45-54': 9, '55-64': 3, '65+': 1 },
        device: { mobile_app: 50, mobile_web: 25, desktop: 20, tablet: 5 }
      },
      'demo_client_004': { // Restaurante - equilibrado, mais velho
        gender: { male: 45, female: 52, unknown: 3 },
        age: { '18-24': 12, '25-34': 28, '35-44': 32, '45-54': 18, '55-64': 7, '65+': 3 },
        device: { mobile_app: 48, mobile_web: 24, desktop: 22, tablet: 6 }
      },
      'demo_client_005': { // Educação - equilibrado
        gender: { male: 52, female: 45, unknown: 3 },
        age: { '18-24': 35, '25-34': 38, '35-44': 18, '45-54': 6, '55-64': 2, '65+': 1 },
        device: { mobile_app: 35, mobile_web: 23, desktop: 38, tablet: 4 }
      }
    };

    const profile = demographicProfiles[clientId] || demographicProfiles['demo_client_001'];
    const geoData = this.getDemoGeographicData(clientId);

    // Total de conversões e impressões base para calcular valores absolutos
    const config = this._getClientMetricsConfig(clientId);
    const baseConversions = (config.dailyConversions?.meta || 20) * 30; // 30 dias de conversões
    const baseImpressions = ((config.dailyImpressions?.meta || 50000) + (config.dailyImpressions?.google || 15000)) * 30;

    // Converter formato de percentuais para formato esperado pela página
    // Formato esperado: { genderAge: [], device: [], region: [] }
    const genderAgeData = [];
    const deviceData = [];
    const regionData = [];

    // Gerar dados de gênero + idade (combinados como a API real retorna)
    Object.entries(profile.gender).forEach(([gender, genderPercent]) => {
      Object.entries(profile.age).forEach(([age, agePercent]) => {
        // Calcula conversões proporcionais
        const combinedPercent = (genderPercent / 100) * (agePercent / 100);
        const conversions = Math.round(baseConversions * combinedPercent);

        if (conversions > 0) {
          genderAgeData.push({
            gender,
            age,
            conversions,
            impressions: Math.round(baseImpressions * combinedPercent),
            // Formato Meta Ads (actions array)
            actions: [
              { action_type: 'omni_purchase', value: Math.round(conversions * 0.3) },
              { action_type: 'lead', value: Math.round(conversions * 0.4) },
              { action_type: 'onsite_conversion.messaging_conversation_started_7d', value: Math.round(conversions * 0.3) }
            ]
          });
        }
      });
    });

    // Gerar dados de dispositivo
    Object.entries(profile.device).forEach(([device, percent]) => {
      const conversions = Math.round(baseConversions * (percent / 100));
      if (conversions > 0) {
        deviceData.push({
          device_platform: device,
          conversions,
          impressions: Math.round(baseImpressions * (percent / 100)),
          actions: [
            { action_type: 'omni_purchase', value: Math.round(conversions * 0.3) },
            { action_type: 'lead', value: Math.round(conversions * 0.4) },
            { action_type: 'onsite_conversion.messaging_conversation_started_7d', value: Math.round(conversions * 0.3) }
          ]
        });
      }
    });

    // Dados regionais já vêm no formato correto do getDemoGeographicData
    geoData.forEach(geo => {
      regionData.push({
        state: geo.state,
        region: geo.state,
        conversions: geo.conversions,
        spend: geo.spend,
        impressions: geo.impressions,
        clicks: geo.clicks,
        reach: geo.reach
      });
    });

    return {
      genderAge: genderAgeData,
      device: deviceData,
      region: regionData
    };
  }

  // ==================== DADOS GEOGRÁFICOS MOCK ====================

  /**
   * Retorna dados geográficos (por estado) mock
   */
  getDemoGeographicData(clientId) {
    // Distribuição base por região
    const geoProfiles = {
      'demo_client_001': { // Nacional - concentrado em SP
        'SP': 35, 'RJ': 15, 'MG': 12, 'RS': 8, 'PR': 7, 'SC': 5,
        'BA': 4, 'DF': 4, 'GO': 3, 'PE': 3, 'CE': 2, 'ES': 2
      },
      'demo_client_002': { // Nacional - moda
        'SP': 32, 'RJ': 18, 'MG': 10, 'RS': 7, 'PR': 6, 'SC': 5,
        'BA': 5, 'DF': 5, 'GO': 4, 'PE': 3, 'CE': 3, 'ES': 2
      },
      'demo_client_003': { // Local - MG (Belo Horizonte)
        'MG': 85, 'SP': 8, 'RJ': 4, 'ES': 2, 'DF': 1
      },
      'demo_client_004': { // Local - PR (Curitiba)
        'PR': 82, 'SC': 10, 'SP': 5, 'RS': 3
      },
      'demo_client_005': { // Nacional - educação online
        'SP': 28, 'RJ': 12, 'MG': 11, 'RS': 8, 'PR': 7, 'SC': 5,
        'BA': 6, 'DF': 5, 'GO': 4, 'PE': 4, 'CE': 4, 'ES': 3, 'PA': 3
      }
    };

    const profile = geoProfiles[clientId] || geoProfiles['demo_client_001'];

    // Converter para formato de mapa
    return Object.entries(profile).map(([state, percentage]) => ({
      state,
      percentage,
      spend: Math.round((percentage / 100) * 5000),
      impressions: Math.round((percentage / 100) * 250000),
      clicks: Math.round((percentage / 100) * 5000),
      reach: Math.round((percentage / 100) * 120000),
      conversions: Math.round((percentage / 100) * 150)
    }));
  }

  // ==================== TIPOS DE CONVERSÃO MOCK ====================

  /**
   * Retorna tipos de conversão mock
   */
  getDemoConversionTypes(clientId) {
    const conversionProfiles = {
      'demo_client_001': [
        { type: 'purchase', name: 'Compras', count: 385, value: 77000, percentage: 65 },
        { type: 'add_to_cart', name: 'Carrinho', count: 1250, value: 0, percentage: 20 },
        { type: 'view_content', name: 'Visualizações', count: 4500, value: 0, percentage: 10 },
        { type: 'lead', name: 'Leads', count: 180, value: 0, percentage: 5 }
      ],
      'demo_client_002': [
        { type: 'purchase', name: 'Compras', count: 245, value: 36750, percentage: 55 },
        { type: 'add_to_cart', name: 'Carrinho', count: 890, value: 0, percentage: 25 },
        { type: 'initiate_checkout', name: 'Checkout', count: 420, value: 0, percentage: 15 },
        { type: 'lead', name: 'Cadastros', count: 85, value: 0, percentage: 5 }
      ],
      'demo_client_003': [
        { type: 'lead', name: 'Leads Captados', count: 485, value: 0, percentage: 70 },
        { type: 'complete_registration', name: 'Matrículas', count: 145, value: 43500, percentage: 25 },
        { type: 'schedule', name: 'Agendamentos', count: 35, value: 0, percentage: 5 }
      ],
      'demo_client_004': [
        { type: 'messaging_conversation_started_7d', name: 'Conversas WhatsApp', count: 520, value: 0, percentage: 75 },
        { type: 'lead', name: 'Reservas', count: 125, value: 18750, percentage: 20 },
        { type: 'other', name: 'Outros', count: 35, value: 0, percentage: 5 }
      ],
      'demo_client_005': [
        { type: 'purchase', name: 'Vendas Cursos', count: 485, value: 242500, percentage: 35 },
        { type: 'lead', name: 'Leads Captados', count: 2850, value: 0, percentage: 55 },
        { type: 'complete_registration', name: 'Cadastros', count: 680, value: 0, percentage: 10 }
      ]
    };

    return conversionProfiles[clientId] || conversionProfiles['demo_client_001'];
  }

  // ==================== RESUMO PARA DASHBOARD ====================

  /**
   * Gera variação realista para métricas diárias
   * Usa um seed baseado na data para manter consistência entre renders
   */
  _generateDailyVariation(dateStr, baseValue, variancePercent = 0.25) {
    // Seed pseudo-random baseado na string da data para consistência
    let hash = 0;
    for (let i = 0; i < dateStr.length; i++) {
      const char = dateStr.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    // Normaliza para 0-1
    const random = Math.abs(Math.sin(hash)) ;

    // Dia da semana (0 = domingo, 6 = sábado)
    const date = new Date(dateStr);
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    // Fins de semana têm menos gastos (-20%)
    const weekendFactor = isWeekend ? 0.8 : 1.0;

    // Variação aleatória baseada no hash
    const variationFactor = 1 + (random - 0.5) * 2 * variancePercent;

    return Math.round(baseValue * weekendFactor * variationFactor * 100) / 100;
  }

  /**
   * Gera resumo consolidado para o dashboard da agência
   * Formato compatível com a estrutura esperada pelo dashboard
   * Inclui dailyData com variação realista para gráficos
   */
  getDemoDashboardSummary(period = 'last_30_days') {
    const clients = this.getDemoClients();
    const days = period === 'last_7_days' ? 7 : 30;

    let totalSpent = 0;
    let totalImpressions = 0;
    let totalClicks = 0;
    let totalPurchases = 0;
    let totalRevenue = 0;
    let totalLeads = 0;
    let totalMessages = 0;

    const clientDetails = {};

    // ==================== GERAR DADOS DIÁRIOS COM VARIAÇÃO ====================
    const dailyData = [];
    const now = new Date();

    // Calcular totais base por dia separados por plataforma
    let baseDailyMetaSpend = 0, baseDailyGoogleSpend = 0;
    let baseDailyMetaLeads = 0, baseDailyGoogleLeads = 0;
    let baseDailyMetaMessages = 0;
    let baseDailyMetaPurchases = 0, baseDailyGooglePurchases = 0;
    let baseDailyMetaRevenue = 0, baseDailyGoogleRevenue = 0;

    clients.forEach(client => {
      const config = this._getClientMetricsConfig(client.id);
      baseDailyMetaSpend += config.dailySpend.meta;
      baseDailyGoogleSpend += config.dailySpend.google || 0;
      baseDailyMetaLeads += config.dailyLeads.meta;
      baseDailyGoogleLeads += config.dailyLeads.google || 0;
      baseDailyMetaMessages += typeof config.dailyMessages === 'object'
        ? config.dailyMessages.meta || 0
        : config.dailyMessages || 0;
      baseDailyMetaPurchases += config.dailySales?.meta || Math.round(config.dailyConversions.meta * 0.3);
      baseDailyGooglePurchases += config.dailySales?.google || Math.round((config.dailyConversions.google || 0) * 0.4);
      baseDailyMetaRevenue += config.dailyRevenue.meta;
      baseDailyGoogleRevenue += config.dailyRevenue.google || 0;
    });

    // Gerar dados para cada dia com separação por plataforma
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD

      // Aplicar variação realista (±25% + efeito de fim de semana)
      const dayMetaSpend = this._generateDailyVariation(dateStr, baseDailyMetaSpend, 0.25);
      const dayGoogleSpend = this._generateDailyVariation(dateStr + '_g', baseDailyGoogleSpend, 0.25);
      const dayMetaLeads = Math.round(this._generateDailyVariation(dateStr + '_ml', baseDailyMetaLeads, 0.30));
      const dayGoogleLeads = Math.round(this._generateDailyVariation(dateStr + '_gl', baseDailyGoogleLeads, 0.30));
      const dayMessages = Math.round(this._generateDailyVariation(dateStr + '_msgs', baseDailyMetaMessages, 0.35));
      const dayMetaPurchases = Math.round(this._generateDailyVariation(dateStr + '_mp', baseDailyMetaPurchases, 0.30));
      const dayGooglePurchases = Math.round(this._generateDailyVariation(dateStr + '_gp', baseDailyGooglePurchases, 0.30));
      const dayMetaRevenue = this._generateDailyVariation(dateStr + '_mr', baseDailyMetaRevenue, 0.28);
      const dayGoogleRevenue = this._generateDailyVariation(dateStr + '_gr', baseDailyGoogleRevenue, 0.28);

      dailyData.push({
        date: dateStr,
        spend: dayMetaSpend + dayGoogleSpend,
        metaSpend: dayMetaSpend,
        googleSpend: dayGoogleSpend,
        leads: dayMetaLeads + dayGoogleLeads,
        metaLeads: dayMetaLeads,
        googleLeads: dayGoogleLeads,
        messages: dayMessages,
        metaMessages: dayMessages,
        purchases: dayMetaPurchases + dayGooglePurchases,
        metaPurchases: dayMetaPurchases,
        googlePurchases: dayGooglePurchases,
        revenue: dayMetaRevenue + dayGoogleRevenue,
        metaRevenue: dayMetaRevenue,
        googleRevenue: dayGoogleRevenue
      });

      // Acumular totais
      totalSpent += dayMetaSpend + dayGoogleSpend;
      totalLeads += dayMetaLeads + dayGoogleLeads;
      totalMessages += dayMessages;
      totalPurchases += dayMetaPurchases + dayGooglePurchases;
      totalRevenue += dayMetaRevenue + dayGoogleRevenue;
    }

    // ==================== CALCULAR CLIENT DETAILS ====================
    clients.forEach(client => {
      const config = this._getClientMetricsConfig(client.id);

      const metaSpend = config.dailySpend.meta * days;
      const googleSpend = (config.dailySpend.google || 0) * days;
      const spend = metaSpend + googleSpend;
      const metaImpressions = config.dailyImpressions.meta * days;
      const googleImpressions = (config.dailyImpressions.google || 0) * days;
      const metaClicks = config.dailyClicks.meta * days;
      const googleClicks = (config.dailyClicks.google || 0) * days;
      const metaRevenue = config.dailyRevenue.meta * days;
      const googleRevenue = (config.dailyRevenue.google || 0) * days;
      const metaLeads = config.dailyLeads.meta * days;
      const googleLeads = (config.dailyLeads.google || 0) * days;
      const metaMessages = (typeof config.dailyMessages === 'object' ? config.dailyMessages.meta : config.dailyMessages || 0) * days;
      const metaPurchases = (config.dailySales?.meta || Math.round(config.dailyConversions.meta * 0.3)) * days;
      const googlePurchases = (config.dailySales?.google || Math.round((config.dailyConversions.google || 0) * 0.4)) * days;

      totalImpressions += metaImpressions + googleImpressions;
      totalClicks += metaClicks + googleClicks;

      // Formato compatível com _compactClient do dashboard-summary.js
      clientDetails[client.id] = {
        name: client.name,
        id: client.id,
        contractStartDate: client.contractStartDate,
        metaSpend: Math.round(metaSpend * 100) / 100,
        googleSpend: Math.round(googleSpend * 100) / 100,
        metaRevenue: Math.round(metaRevenue * 100) / 100,
        googleRevenue: Math.round(googleRevenue * 100) / 100,
        metaImpressions: Math.round(metaImpressions),
        googleImpressions: Math.round(googleImpressions),
        metaClicks: Math.round(metaClicks),
        googleClicks: Math.round(googleClicks),
        metaPurchases: Math.round(metaPurchases),
        googlePurchases: Math.round(googlePurchases),
        metaMessages: Math.round(metaMessages),
        metaLeads: Math.round(metaLeads),
        googleLeads: Math.round(googleLeads)
      };
    });

    // Formato compatível com getEmptyMetrics() do dashboard
    return {
      totalSpent: Math.round(totalSpent * 100) / 100,
      totalRevenue: Math.round(totalRevenue * 100) / 100,
      totalPurchases: Math.round(totalPurchases),
      totalMessages: Math.round(totalMessages),
      totalLeads: Math.round(totalLeads),
      totalImpressions: Math.round(totalImpressions),
      totalClicks: Math.round(totalClicks),
      roas: totalSpent > 0 ? Math.round((totalRevenue / totalSpent) * 100) / 100 : 0,
      clientDetails,
      dailyData, // Dados diários para gráficos com variação realista
      // Campos adicionais úteis
      period,
      activeClients: clients.filter(c => c.status === 'active').length,
      totalClients: clients.length,
      isDemo: true
    };
  }

  // ==================== AD SETS E CRIATIVOS MOCK ====================

  /**
   * Retorna ad sets mock para um cliente
   */
  getDemoAdSets(clientId) {
    const adSetTemplates = {
      'demo_client_001': [
        { id: 'adset_001_1', name: 'Remarketing - Visitantes 7d', status: 'ACTIVE', campaignId: 'camp_001_1', campaign_id: 'camp_001_1', platform: 'meta', spend: 1800, impressions: 82000, clicks: 2100, conversions: 52, revenue: 10400, optimization_goal: 'OFFSITE_CONVERSIONS', roas: 5.8 },
        { id: 'adset_001_2', name: 'Remarketing - Carrinho 3d', status: 'ACTIVE', campaignId: 'camp_001_1', campaign_id: 'camp_001_1', platform: 'meta', spend: 2400, impressions: 103000, clicks: 2700, conversions: 90, revenue: 18100, optimization_goal: 'OFFSITE_CONVERSIONS', roas: 7.2 },
        { id: 'adset_001_3', name: 'Lookalike - Compradores 1%', status: 'ACTIVE', campaignId: 'camp_001_2', campaign_id: 'camp_001_2', platform: 'meta', spend: 2800, impressions: 160000, clicks: 3100, conversions: 48, revenue: 9800, optimization_goal: 'OFFSITE_CONVERSIONS', roas: 3.2 },
        { id: 'adset_001_4', name: 'Interesses - Tech Gadgets', status: 'PAUSED', campaignId: 'camp_001_2', campaign_id: 'camp_001_2', platform: 'meta', spend: 1200, impressions: 58000, clicks: 1200, conversions: 22, revenue: 4200, optimization_goal: 'OFFSITE_CONVERSIONS', roas: 2.8 }
      ],
      'demo_client_002': [
        { id: 'adset_002_1', name: 'Moda Feminina 25-44', status: 'ACTIVE', campaignId: 'camp_002_1', campaign_id: 'camp_002_1', platform: 'meta', spend: 2200, impressions: 180000, clicks: 3600, conversions: 68, revenue: 10200, optimization_goal: 'OFFSITE_CONVERSIONS', roas: 4.1 },
        { id: 'adset_002_2', name: 'Instagram Engajamento', status: 'ACTIVE', campaignId: 'camp_002_2', campaign_id: 'camp_002_2', platform: 'meta', spend: 1400, impressions: 290000, clicks: 6250, conversions: 0, revenue: 0, optimization_goal: 'POST_ENGAGEMENT', roas: 0 },
        { id: 'adset_002_3', name: 'Lookalike Compradoras', status: 'ACTIVE', campaignId: 'camp_002_1', campaign_id: 'camp_002_1', platform: 'meta', spend: 1800, impressions: 130000, clicks: 2600, conversions: 45, revenue: 6750, optimization_goal: 'OFFSITE_CONVERSIONS', roas: 3.5 }
      ],
      'demo_client_003': [
        { id: 'adset_003_1', name: 'Público Local 5km', status: 'ACTIVE', campaignId: 'camp_003_1', campaign_id: 'camp_003_1', platform: 'meta', spend: 1680, impressions: 72000, clicks: 1450, conversions: 125, revenue: 0, optimization_goal: 'LEAD_GENERATION', roas: 0 },
        { id: 'adset_003_2', name: 'Interesse Fitness 25-45', status: 'ACTIVE', campaignId: 'camp_003_1', campaign_id: 'camp_003_1', platform: 'meta', spend: 1680, impressions: 73000, clicks: 1450, conversions: 120, revenue: 0, optimization_goal: 'LEAD_GENERATION', roas: 0 }
      ],
      'demo_client_004': [
        { id: 'adset_004_1', name: 'Público Restaurantes 3km', status: 'ACTIVE', campaignId: 'camp_004_1', campaign_id: 'camp_004_1', platform: 'meta', spend: 1120, impressions: 49000, clicks: 920, conversions: 210, revenue: 0, optimization_goal: 'CONVERSATIONS', roas: 0 },
        { id: 'adset_004_2', name: 'Foodies Instagram', status: 'ACTIVE', campaignId: 'camp_004_1', campaign_id: 'camp_004_1', platform: 'meta', spend: 1120, impressions: 49000, clicks: 930, conversions: 210, revenue: 0, optimization_goal: 'CONVERSATIONS', roas: 0 }
      ],
      'demo_client_005': [
        { id: 'adset_005_1', name: 'Interesse Programação 18-35', status: 'ACTIVE', campaignId: 'camp_005_1', campaign_id: 'camp_005_1', platform: 'meta', spend: 4900, impressions: 260000, clicks: 7800, conversions: 145, revenue: 72500, optimization_goal: 'OFFSITE_CONVERSIONS', roas: 14.5 },
        { id: 'adset_005_2', name: 'Lookalike Compradores', status: 'ACTIVE', campaignId: 'camp_005_1', campaign_id: 'camp_005_1', platform: 'meta', spend: 4900, impressions: 260000, clicks: 7800, conversions: 140, revenue: 70000, optimization_goal: 'OFFSITE_CONVERSIONS', roas: 14.3 },
        { id: 'adset_005_3', name: 'Estudantes Universitários', status: 'ACTIVE', campaignId: 'camp_005_2', campaign_id: 'camp_005_2', platform: 'meta', spend: 2800, impressions: 190000, clicks: 4750, conversions: 925, revenue: 0, optimization_goal: 'LEAD_GENERATION', roas: 0 },
        { id: 'adset_005_4', name: 'Profissionais TI', status: 'ACTIVE', campaignId: 'camp_005_2', campaign_id: 'camp_005_2', platform: 'meta', spend: 2800, impressions: 190000, clicks: 4750, conversions: 925, revenue: 0, optimization_goal: 'LEAD_GENERATION', roas: 0 }
      ]
    };

    return adSetTemplates[clientId] || adSetTemplates['demo_client_001'];
  }

  /**
   * Retorna ad groups Google Ads demo para um cliente
   */
  getDemoGoogleAdGroups(clientId) {
    const config = this._getClientMetricsConfig(clientId);
    if (!config.hasGoogle) return [];

    const googleAdGroupTemplates = {
      'demo_client_001': [
        { id: 'gadg_001_1', name: 'Smartphones Samsung', status: 'ACTIVE', campaignId: 'gcamp_001_1', campaign_id: 'gcamp_001_1', platform: 'google', spend: 2100, impressions: 62000, clicks: 2900, conversions: 95, revenue: 16500, optimization_goal: 'CONVERSIONS' },
        { id: 'gadg_001_2', name: 'Notebooks e Tablets', status: 'ACTIVE', campaignId: 'gcamp_001_1', campaign_id: 'gcamp_001_1', platform: 'google', spend: 2100, impressions: 63000, clicks: 2900, conversions: 90, revenue: 16000, optimization_goal: 'CONVERSIONS' },
        { id: 'gadg_001_3', name: 'Shopping - Eletrônicos', status: 'ACTIVE', campaignId: 'gcamp_001_2', campaign_id: 'gcamp_001_2', platform: 'google', spend: 2800, impressions: 85000, clicks: 3200, conversions: 95, revenue: 18500, optimization_goal: 'CONVERSIONS' }
      ],
      'demo_client_002': [
        { id: 'gadg_002_1', name: 'Shopping - Vestidos', status: 'ACTIVE', campaignId: 'gcamp_002_1', campaign_id: 'gcamp_002_1', platform: 'google', spend: 1120, impressions: 36000, clicks: 925, conversions: 34, revenue: 4250, optimization_goal: 'CONVERSIONS' },
        { id: 'gadg_002_2', name: 'Shopping - Acessórios', status: 'ACTIVE', campaignId: 'gcamp_002_1', campaign_id: 'gcamp_002_1', platform: 'google', spend: 1120, impressions: 36000, clicks: 925, conversions: 34, revenue: 4250, optimization_goal: 'CONVERSIONS' }
      ],
      'demo_client_003': [
        { id: 'gadg_003_1', name: 'Academia Perto de Mim', status: 'ACTIVE', campaignId: 'gcamp_003_1', campaign_id: 'gcamp_003_1', platform: 'google', spend: 840, impressions: 21000, clicks: 925, conversions: 65, revenue: 0, optimization_goal: 'LEADS' },
        { id: 'gadg_003_2', name: 'Personal Trainer', status: 'ACTIVE', campaignId: 'gcamp_003_1', campaign_id: 'gcamp_003_1', platform: 'google', spend: 840, impressions: 21000, clicks: 925, conversions: 60, revenue: 0, optimization_goal: 'LEADS' }
      ],
      'demo_client_005': [
        { id: 'gadg_005_1', name: 'Curso Python Online', status: 'ACTIVE', campaignId: 'gcamp_005_1', campaign_id: 'gcamp_005_1', platform: 'google', spend: 2800, impressions: 82000, clicks: 3400, conversions: 125, revenue: 24500, optimization_goal: 'CONVERSIONS' },
        { id: 'gadg_005_2', name: 'Programação Iniciante', status: 'ACTIVE', campaignId: 'gcamp_005_1', campaign_id: 'gcamp_005_1', platform: 'google', spend: 2800, impressions: 83000, clicks: 3400, conversions: 120, revenue: 24000, optimization_goal: 'CONVERSIONS' },
        { id: 'gadg_005_3', name: 'YouTube - Aulas Grátis', status: 'ACTIVE', campaignId: 'gcamp_005_2', campaign_id: 'gcamp_005_2', platform: 'google', spend: 4200, impressions: 350000, clicks: 8500, conversions: 580, revenue: 0, optimization_goal: 'LEADS' }
      ]
    };

    return googleAdGroupTemplates[clientId] || [];
  }

  /**
   * Retorna criativos mock para um cliente
   */
  getDemoCreatives(clientId) {
    const creativeTemplates = {
      'demo_client_001': [
        { id: 'creative_001_1', name: 'Carrossel Smartphones', type: 'carousel', spend: 1800, impressions: 82000, clicks: 2100, ctr: 2.6, conversions: 52, revenue: 10400, campaign_id: 'camp_001_1', campaign_name: 'TechStore - Remarketing Carrinho', adset_id: 'adset_001_1', adset_name: 'Remarketing - Visitantes 7d', thumbnail_url: 'https://picsum.photos/seed/tech1/400/400' },
        { id: 'creative_001_2', name: 'Video Unboxing', type: 'video', spend: 2400, impressions: 103000, clicks: 2700, ctr: 2.6, conversions: 90, revenue: 18100, campaign_id: 'camp_001_1', campaign_name: 'TechStore - Remarketing Carrinho', adset_id: 'adset_001_2', adset_name: 'Remarketing - Carrinho 3d', thumbnail_url: 'https://picsum.photos/seed/tech2/400/400' },
        { id: 'creative_001_3', name: 'Imagem Promoção', type: 'image', spend: 2800, impressions: 160000, clicks: 3100, ctr: 1.9, conversions: 48, revenue: 9800, campaign_id: 'camp_001_2', campaign_name: 'TechStore - Prospecção Lookalike', adset_id: 'adset_001_3', adset_name: 'Lookalike - Compradores 1%', thumbnail_url: 'https://picsum.photos/seed/tech3/400/400' },
        { id: 'creative_001_4', name: 'Banner Tech Gadgets', type: 'image', spend: 1200, impressions: 58000, clicks: 1200, ctr: 2.1, conversions: 22, revenue: 4200, campaign_id: 'camp_001_2', campaign_name: 'TechStore - Prospecção Lookalike', adset_id: 'adset_001_4', adset_name: 'Interesses - Tech Gadgets', thumbnail_url: 'https://picsum.photos/seed/tech4/400/400' }
      ],
      'demo_client_002': [
        { id: 'creative_002_1', name: 'Coleção Verão - Carrossel', type: 'carousel', spend: 2200, impressions: 180000, clicks: 3600, ctr: 2.0, conversions: 68, revenue: 10200, campaign_id: 'camp_002_1', campaign_name: 'Bella Moda - Coleção Verão', adset_id: 'adset_002_1', adset_name: 'Moda Feminina 25-44', thumbnail_url: 'https://picsum.photos/seed/fashion1/400/400' },
        { id: 'creative_002_2', name: 'Look do Dia - Reels', type: 'video', spend: 1400, impressions: 290000, clicks: 6250, ctr: 2.2, conversions: 0, revenue: 0, campaign_id: 'camp_002_2', campaign_name: 'Bella Moda - Instagram Engajamento', adset_id: 'adset_002_2', adset_name: 'Instagram Engajamento', thumbnail_url: 'https://picsum.photos/seed/fashion2/400/400' },
        { id: 'creative_002_3', name: 'Stories Promocional', type: 'story', spend: 1800, impressions: 130000, clicks: 2600, ctr: 2.0, conversions: 45, revenue: 6750, campaign_id: 'camp_002_1', campaign_name: 'Bella Moda - Coleção Verão', adset_id: 'adset_002_3', adset_name: 'Lookalike Compradoras', thumbnail_url: 'https://picsum.photos/seed/fashion3/400/400' }
      ],
      'demo_client_003': [
        { id: 'creative_003_1', name: 'Aula Experimental Grátis', type: 'image', spend: 1680, impressions: 72000, clicks: 1450, ctr: 2.0, conversions: 125, revenue: 0, campaign_id: 'camp_003_1', campaign_name: 'FitLife - Captação Leads Janeiro', adset_id: 'adset_003_1', adset_name: 'Público Local 5km', thumbnail_url: 'https://picsum.photos/seed/fitness1/400/400' },
        { id: 'creative_003_2', name: 'Antes e Depois - Video', type: 'video', spend: 1680, impressions: 73000, clicks: 1450, ctr: 2.0, conversions: 120, revenue: 0, campaign_id: 'camp_003_1', campaign_name: 'FitLife - Captação Leads Janeiro', adset_id: 'adset_003_2', adset_name: 'Interesse Fitness 25-45', thumbnail_url: 'https://picsum.photos/seed/fitness2/400/400' }
      ],
      'demo_client_004': [
        { id: 'creative_004_1', name: 'Prato do Dia - Carrossel', type: 'carousel', spend: 1120, impressions: 49000, clicks: 920, ctr: 1.9, conversions: 210, revenue: 0, campaign_id: 'camp_004_1', campaign_name: 'Sabor Arte - Reservas WhatsApp', adset_id: 'adset_004_1', adset_name: 'Público Restaurantes 3km', thumbnail_url: 'https://picsum.photos/seed/food1/400/400' },
        { id: 'creative_004_2', name: 'Chef Preparando - Reels', type: 'video', spend: 1120, impressions: 49000, clicks: 930, ctr: 1.9, conversions: 210, revenue: 0, campaign_id: 'camp_004_1', campaign_name: 'Sabor Arte - Reservas WhatsApp', adset_id: 'adset_004_2', adset_name: 'Foodies Instagram', thumbnail_url: 'https://picsum.photos/seed/food2/400/400' }
      ],
      'demo_client_005': [
        { id: 'creative_005_1', name: 'Aula Grátis Python - Carrossel', type: 'carousel', spend: 4900, impressions: 260000, clicks: 7800, ctr: 3.0, conversions: 145, revenue: 72500, campaign_id: 'camp_005_1', campaign_name: 'EduTech - Curso Python Full', adset_id: 'adset_005_1', adset_name: 'Interesse Programação 18-35', thumbnail_url: 'https://picsum.photos/seed/edu1/400/400' },
        { id: 'creative_005_2', name: 'Depoimento Aluno - Video', type: 'video', spend: 4900, impressions: 260000, clicks: 7800, ctr: 3.0, conversions: 140, revenue: 70000, campaign_id: 'camp_005_1', campaign_name: 'EduTech - Curso Python Full', adset_id: 'adset_005_2', adset_name: 'Lookalike Compradores', thumbnail_url: 'https://picsum.photos/seed/edu2/400/400' },
        { id: 'creative_005_3', name: 'Oferta Limitada - Imagem', type: 'image', spend: 2800, impressions: 190000, clicks: 4750, ctr: 2.5, conversions: 925, revenue: 0, campaign_id: 'camp_005_2', campaign_name: 'EduTech - Leads Gratuito', adset_id: 'adset_005_3', adset_name: 'Estudantes Universitários', thumbnail_url: 'https://picsum.photos/seed/edu3/400/400' }
      ]
    };

    return creativeTemplates[clientId] || creativeTemplates['demo_client_001'];
  }

  // ==================== MÉTRICAS COMPLETAS PARA PERFIL DO CLIENTE ====================

  /**
   * Retorna métricas completas formatadas para a página de perfil do cliente
   * Inclui dailyData, totais, campanhas, ad sets, criativos e todos os dados necessários
   *
   * FORMATO CRÍTICO: dailyData deve ter formato plano esperado pela página:
   * { 'YYYY-MM-DD': { spend, metaSpend, googleSpend, impressions, clicks, ... } }
   */
  getDemoClientMetrics(clientId, since, until) {
    const config = this._getClientMetricsConfig(clientId);
    const campaigns = this.getDemoCampaigns(clientId);
    const adSets = this.getDemoAdSets(clientId);
    const creatives = this.getDemoCreatives(clientId);
    const conversionTypes = this.getDemoConversionTypes(clientId);
    const geoData = this.getDemoGeographicData(clientId);

    // Usar datas fornecidas ou padrão 30 dias
    let startDateStr, endDateStr;
    if (since && until) {
      startDateStr = since;
      endDateStr = until;
    } else {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      startDateStr = formatDateISO(startDate);
      endDateStr = formatDateISO(endDate);
    }

    // Gerar dados diários no formato interno
    const rawDailyData = this.generateDailyMetrics(clientId, startDateStr, endDateStr);

    // ==================== CONVERTER PARA FORMATO DA PÁGINA ====================
    // A página espera: { 'YYYY-MM-DD': { spend, metaSpend, googleSpend, ... } }
    const dailyData = {};
    let totalSpend = 0, totalImpressions = 0, totalClicks = 0, totalConversions = 0;
    let totalRevenue = 0, totalLeads = 0, totalMessages = 0, totalProfileVisits = 0;
    let totalReach = 0;

    Object.entries(rawDailyData).forEach(([dateStr, day]) => {
      // Formato plano esperado pela página de perfil
      dailyData[dateStr] = {
        // Valores combinados (totais)
        spend: day.combined.spend,
        impressions: day.combined.impressions,
        clicks: day.combined.clicks,
        conversions: day.combined.conversions,
        conversionValue: day.combined.conversionValue,

        // Valores Meta Ads
        metaSpend: day.meta.spend,
        metaImpressions: day.meta.impressions,
        metaClicks: day.meta.clicks,
        metaConversions: day.meta.conversions,
        metaRevenue: day.meta.conversionValue,
        metaReach: day.meta.reach,
        metaLeads: day.meta.leads,
        metaMessages: day.meta.messages || 0,
        metaProfileVisits: day.meta.profileVisits || 0,

        // Valores Google Ads
        googleSpend: day.google?.spend || 0,
        googleImpressions: day.google?.impressions || 0,
        googleClicks: day.google?.clicks || 0,
        googleConversions: day.google?.conversions || 0,
        googleRevenue: day.google?.conversionValue || 0,
        googleReach: day.google?.reach || 0, // Google Display/YouTube tem reach
        googleLeads: day.google?.leads || 0,
        googleMessages: day.google?.messages || 0,
        googleSales: day.google?.sales || 0,

        // Valores de vendas por plataforma
        metaSales: day.meta.sales || 0,

        // Métricas derivadas
        ctr: day.combined.impressions > 0
          ? ((day.combined.clicks / day.combined.impressions) * 100).toFixed(2)
          : 0,
        cpc: day.combined.clicks > 0
          ? (day.combined.spend / day.combined.clicks).toFixed(2)
          : 0,

        // Métricas de engajamento
        leads: day.meta.leads || 0,
        messages: day.meta.messages || 0,
        reach: day.meta.reach || 0,

        // Conversões por tipo (para gráficos de leads/mensagens)
        conversionsByType: day.conversionsByType || {}
      };

      // Acumular totais
      totalSpend += day.combined.spend;
      totalImpressions += day.combined.impressions;
      totalClicks += day.combined.clicks;
      totalConversions += day.combined.conversions;
      totalRevenue += day.combined.conversionValue;
      totalLeads += day.meta.leads || 0;
      totalMessages += day.meta.messages || 0;
      totalProfileVisits += day.meta.profileVisits || 0;
      totalReach += day.meta.reach || 0;
    });

    // Calcular métricas derivadas
    const ctr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
    const cpc = totalClicks > 0 ? totalSpend / totalClicks : 0;
    const cpa = totalConversions > 0 ? totalSpend / totalConversions : 0;
    const cpl = totalLeads > 0 ? totalSpend / totalLeads : 0;
    const roas = totalSpend > 0 ? totalRevenue / totalSpend : 0;
    const frequency = totalReach > 0 ? totalImpressions / totalReach : 0;

    // ==================== CONVERSIONS BY TYPE ====================
    // Formato esperado: { 'action_type': { count: number, value: number } }
    const conversionsByType = {};
    conversionTypes.forEach(ct => {
      // Mapear tipo para action_type do Meta
      const actionType = ct.type === 'purchase' ? 'omni_purchase'
        : ct.type === 'lead' ? 'lead'
        : ct.type === 'messaging_conversation_started_7d' ? 'onsite_conversion.messaging_conversation_started_7d'
        : ct.type === 'add_to_cart' ? 'omni_add_to_cart'
        : ct.type === 'initiate_checkout' ? 'omni_initiated_checkout'
        : ct.type === 'complete_registration' ? 'omni_complete_registration'
        : ct.type === 'schedule' ? 'onsite_conversion.lead_grouped'
        : ct.type;

      conversionsByType[actionType] = {
        count: ct.count,
        value: ct.value || 0
      };
    });

    // Adicionar visitas ao perfil do Instagram se houver
    if (totalProfileVisits > 0) {
      conversionsByType['onsite_conversion.ig_profile_visit'] = {
        count: totalProfileVisits,
        value: 0
      };
    }

    // Adicionar mensagens ao conversionsByType se houver (e não já existir)
    if (totalMessages > 0 && !conversionsByType['onsite_conversion.messaging_conversation_started_7d']) {
      conversionsByType['onsite_conversion.messaging_conversation_started_7d'] = {
        count: Math.round(totalMessages),
        value: 0
      };
    }

    // ========== ADICIONAR CONVERSÕES DO GOOGLE (TOTAIS) ==========
    // Agregar dados do Google a partir dos dailyData
    let totalGoogleLeads = 0;
    let totalGoogleMessages = 0;
    let totalGooglePurchases = 0;
    let totalGooglePurchaseValue = 0;

    Object.values(rawDailyData).forEach(day => {
      if (day.conversionsByType) {
        // Leads Google
        ['google_submit_lead_form', 'google_qualified_lead', 'google_contact', 'google_signup'].forEach(type => {
          if (day.conversionsByType[type]) {
            const val = day.conversionsByType[type];
            totalGoogleLeads += typeof val === 'object' ? (val.count || 0) : val;
          }
        });
        // Mensagens Google
        ['google_contact_message', 'google_get_directions'].forEach(type => {
          if (day.conversionsByType[type]) {
            const val = day.conversionsByType[type];
            totalGoogleMessages += typeof val === 'object' ? (val.count || 0) : val;
          }
        });
        // Vendas Google
        if (day.conversionsByType['google_purchase']) {
          const val = day.conversionsByType['google_purchase'];
          totalGooglePurchases += typeof val === 'object' ? (val.count || 0) : val;
          totalGooglePurchaseValue += typeof val === 'object' ? (val.value || 0) : 0;
        }
      }
    });

    // Adicionar tipos de conversão do Google aos totais
    if (totalGoogleLeads > 0) {
      // Dividir entre os dois tipos principais
      const submitLeadForm = Math.round(totalGoogleLeads * 0.6);
      const qualifiedLead = totalGoogleLeads - submitLeadForm;
      if (submitLeadForm > 0) {
        conversionsByType['google_submit_lead_form'] = { count: submitLeadForm, value: 0 };
      }
      if (qualifiedLead > 0) {
        conversionsByType['google_qualified_lead'] = { count: qualifiedLead, value: 0 };
      }
    }
    if (totalGoogleMessages > 0) {
      conversionsByType['google_contact_message'] = { count: totalGoogleMessages, value: 0 };
    }
    if (totalGooglePurchases > 0) {
      conversionsByType['google_purchase'] = { count: totalGooglePurchases, value: totalGooglePurchaseValue };
    }

    return {
      // Dados diários para gráficos (formato plano!)
      dailyData,

      // Métricas principais (nível raiz, não em totals)
      spend: Math.round(totalSpend * 100) / 100,
      impressions: Math.round(totalImpressions),
      reach: Math.round(totalReach),
      clicks: Math.round(totalClicks),
      totalConversions: Math.round(totalConversions),
      revenue: Math.round(totalRevenue * 100) / 100,
      leads: Math.round(totalLeads),
      messages: Math.round(totalMessages),
      profileVisits: Math.round(totalProfileVisits),
      ctr: Math.round(ctr * 100) / 100,
      cpc: Math.round(cpc * 100) / 100,
      cpa: Math.round(cpa * 100) / 100,
      cpl: Math.round(cpl * 100) / 100,
      roas: Math.round(roas * 100) / 100,
      frequency: Math.round(frequency * 100) / 100,

      // Tipos de conversão no formato esperado
      conversionsByType,

      // Estrutura de campanhas (Meta + Google combinados)
      campaignsData: [
        // Campanhas Meta Ads
        ...campaigns.map(c => ({
          ...c,
          platform: 'meta'
        })),
        // Campanhas Google Ads
        ...this.getDemoGoogleCampaigns(clientId).map(c => ({
          ...c,
          platform: 'google'
        }))
      ],

      // Ad Sets (Meta + Google ad groups combinados)
      adSetsData: [
        ...adSets,
        ...this.getDemoGoogleAdGroups(clientId)
      ],

      // Criativos
      creativesData: creatives.map(cr => ({
        ...cr,
        platform: 'meta'
      })),

      // Dados geográficos
      geographicData: geoData,

      // Dados de awareness/reconhecimento
      awarenessData: {
        impressions: Math.round(totalImpressions),
        reach: Math.round(totalReach),
        frequency: Math.round(frequency * 100) / 100,
        profileVisits: Math.round(totalProfileVisits),
        videoViews: Math.round(totalImpressions * 0.15),
        videoViews25: Math.round(totalImpressions * 0.12),
        videoViews50: Math.round(totalImpressions * 0.08),
        videoViews75: Math.round(totalImpressions * 0.05),
        videoViews100: Math.round(totalImpressions * 0.03),
        postEngagement: Math.round(totalClicks * 0.8),
        postReactions: Math.round(totalClicks * 0.5),
        postComments: Math.round(totalClicks * 0.1),
        postShares: Math.round(totalClicks * 0.05),
        postSaves: Math.round(totalClicks * 0.15)
      },

      // Dados de mensagens
      messagesData: {
        total: Math.round(totalMessages),
        started: Math.round(totalMessages * 0.9),
        replied: Math.round(totalMessages * 0.7),
        costPerMessage: totalMessages > 0 ? Math.round((totalSpend / totalMessages) * 100) / 100 : 0
      },

      // Período
      period: {
        startDate: startDateStr,
        endDate: endDateStr,
        days: 30
      },

      // Flag indicando que são dados demo
      isDemo: true,

      // Flag crítica para renderização da página de perfil
      hasData: true
    };
  }

  /**
   * Retorna dados mock para o card do cliente na lista de clientes
   * Formato compatível com o retorno de getMetaAdsData/getGoogleAdsData
   * Usado por loadClientDataLazily em clients-page.js
   */
  getDemoClientCardData(clientId) {
    const config = this._getClientMetricsConfig(clientId);
    const client = this.getDemoClients().find(c => c.id === clientId);
    const clientName = client?.name || 'Demo Account';
    const hasGoogle = config.hasGoogle && client?.googleAdAccountIds?.length > 0;

    // Gerar 7 dias de dados (últimos 7 dias como nos cards reais)
    const dailySpending = [];
    const dailyConversions = [];
    const dailyLeads = [];
    const dailyMessages = [];
    const dailyPurchases = [];
    const dailyCPA = [];
    const dailyROAS = [];
    const dailyReach = [];

    // Extrair valores base da config (usar Meta como plataforma principal)
    const baseSpend = config.dailySpend?.meta || 500;
    const baseLeads = config.dailyLeads?.meta || 15;
    const baseMessages = typeof config.dailyMessages === 'object'
      ? (config.dailyMessages.meta || 0) + (config.dailyMessages.google || 0)
      : config.dailyMessages || 30;
    const baseConversions = config.dailyConversions?.meta || 20;
    const baseRevenue = config.dailyRevenue?.meta || 3000;
    const baseReach = config.dailyReach?.meta || 25000;

    for (let i = 0; i < 7; i++) {
      // Variances INDEPENDENTES por métrica (simula flutuação real de campanha)
      const spendVar = 0.80 + Math.random() * 0.40;    // ±20% spend
      const reachVar = 0.70 + Math.random() * 0.60;    // ±30% reach
      const leadsVar = 0.55 + Math.random() * 0.90;    // ±45% leads (alta flutuação)
      const msgsVar = 0.60 + Math.random() * 0.80;     // ±40% mensagens
      const salesVar = 0.50 + Math.random() * 1.00;    // ±50% vendas (maior flutuação)
      const revenueVar = 0.45 + Math.random() * 1.10;  // ±55% receita (ticket varia muito)

      // Efeito dia da semana (seg-dom): fins de semana = mais conversão, menos spend
      const dayOfWeek = (new Date().getDay() + i) % 7; // 0=dom, 6=sab
      const weekendBoost = (dayOfWeek === 0 || dayOfWeek === 6) ? 1.15 : 1.0;
      const weekdaySpendBoost = (dayOfWeek >= 1 && dayOfWeek <= 5) ? 1.05 : 0.85;

      // Spending diário
      const spend = Math.round(baseSpend * spendVar * weekdaySpendBoost);
      dailySpending.push(spend);

      // Reach diário (alcance)
      const reach = Math.round(baseReach * reachVar * weekendBoost);
      dailyReach.push(reach);

      // Leads (formulários + pixel) - varia independente do spend
      const leads = Math.max(1, Math.round(baseLeads * leadsVar * weekendBoost));
      dailyLeads.push(leads);

      // Mensagens (WhatsApp/Instagram) - fim de semana gera mais msgs
      const messages = Math.max(1, Math.round(baseMessages * msgsVar * weekendBoost));
      dailyMessages.push(messages);

      // Compras/Vendas - alta variação (pode ter dias ótimos e dias fracos)
      const purchases = Math.max(0, Math.round(baseConversions * salesVar * 0.3 * weekendBoost));
      dailyPurchases.push(purchases);

      // Total de conversões (leads + mensagens + vendas)
      const totalConversions = leads + messages + purchases;
      dailyConversions.push(totalConversions);

      // CPA (Custo por Aquisição) - reflete variação real
      const leadsAndMessages = leads + messages;
      const cpa = leadsAndMessages > 0 ? Math.round(spend / leadsAndMessages) : 0;
      dailyCPA.push(cpa);

      // ROAS (Return on Ad Spend) - variance independente de revenue vs spend = ROAS flutua
      const revenue = Math.round(baseRevenue * revenueVar * weekendBoost);
      const roas = spend > 0 ? Math.round((revenue / spend) * 100) / 100 : 0;
      dailyROAS.push(roas);
    }

    // ==================== BALANCE META ADS ====================
    const metaBalanceConfigs = {
      'demo_client_001': { accountType: 'prepaid', prepaidBalance: 2850, currency: 'BRL' },
      'demo_client_002': { accountType: 'postpaid_with_limit', creditLimit: 15000, amountSpent: 8500, currency: 'BRL' },
      'demo_client_003': { accountType: 'prepaid', prepaidBalance: 1200, currency: 'BRL' },
      'demo_client_004': { accountType: 'postpaid_with_limit', creditLimit: 8000, amountSpent: 4200, currency: 'BRL' },
      'demo_client_005': { accountType: 'prepaid', prepaidBalance: 3500, currency: 'BRL' }
    };

    const metaBalanceConfig = metaBalanceConfigs[clientId] || metaBalanceConfigs['demo_client_001'];

    const balance = {
      balance: metaBalanceConfig.accountType === 'prepaid'
        ? metaBalanceConfig.prepaidBalance
        : metaBalanceConfig.creditLimit - metaBalanceConfig.amountSpent,
      currency: metaBalanceConfig.currency,
      source: 'Meta Ads',
      hasData: true,
      accountType: metaBalanceConfig.accountType,
      creditLimit: metaBalanceConfig.creditLimit || null,
      prepaidBalance: metaBalanceConfig.prepaidBalance || null,
      amountSpent: metaBalanceConfig.amountSpent || 0,
      spendCap: metaBalanceConfig.creditLimit || 0,
      paymentMethod: metaBalanceConfig.accountType === 'prepaid' ? 'prepaid' : 'VISA',
      cardLast4: metaBalanceConfig.accountType === 'prepaid' ? null : '4832',
      fundingType: metaBalanceConfig.accountType === 'prepaid' ? 20 : 1,
      fundingDisplay: metaBalanceConfig.accountType === 'prepaid'
        ? `Saldo disponível (${regionService.getCurrencySymbol()}${metaBalanceConfig.prepaidBalance.toLocaleString(localeService.getLocale())} BRL)`
        : `VISA *4832`,
      totalCoupons: 0,
      activeCoupons: 0,
      coupons: [],
      accountName: clientName,
      isDemo: true
    };

    // ==================== BALANCE GOOGLE ADS ====================
    let googleBalance = null;
    if (hasGoogle) {
      const googleBalanceConfigs = {
        'demo_client_001': { accountType: 'prepaid', prepaidBalance: 1580, budgetRemaining: 1580 },
        'demo_client_002': { accountType: 'postpaid', creditLimit: 5000, amountSpent: 2100, budgetRemaining: 2900 },
        'demo_client_003': { accountType: 'prepaid', prepaidBalance: 680, budgetRemaining: 680 },
        'demo_client_004': { accountType: 'prepaid', prepaidBalance: 0, budgetRemaining: 0 }, // Sabor & Arte não usa Google
        'demo_client_005': { accountType: 'postpaid', creditLimit: 8000, amountSpent: 4500, budgetRemaining: 3500 }
      };

      const googleConfig = googleBalanceConfigs[clientId] || googleBalanceConfigs['demo_client_001'];

      // Apenas retorna googleBalance se o cliente realmente usa Google (tem orçamento)
      if (googleConfig.budgetRemaining > 0 || googleConfig.amountSpent > 0) {
        googleBalance = {
          balance: googleConfig.budgetRemaining,
          budgetRemaining: googleConfig.budgetRemaining,
          currency: 'BRL',
          source: 'Google Ads',
          hasData: true,
          accountType: googleConfig.accountType,
          creditLimit: googleConfig.creditLimit || null,
          prepaidBalance: googleConfig.prepaidBalance || null,
          amountSpent: googleConfig.amountSpent || 0,
          // Google Ads usa "Orçamento Restante" ao invés de saldo
          paymentMethod: googleConfig.accountType === 'prepaid' ? 'prepaid' : 'invoice',
          accountName: `${clientName} - Google`,
          isDemo: true
        };
      }
    }

    return {
      spending: { data: dailySpending, isReal: false, source: 'Demo', isDemo: true },
      conversions: { data: dailyConversions, isReal: false, source: 'Demo', isDemo: true },
      leads: { data: dailyLeads.map((l, i) => l + dailyMessages[i]), isReal: false, source: 'Demo', isDemo: true },
      leadsOnly: { data: dailyLeads, isReal: false, source: 'Demo', isDemo: true },
      messages: { data: dailyMessages, isReal: false, source: 'Demo', isDemo: true },
      purchases: { data: dailyPurchases, isReal: false, source: 'Demo', isDemo: true },
      cpa: { data: dailyCPA, isReal: false, source: 'Demo', isDemo: true },
      roas: { data: dailyROAS, isReal: false, source: 'Demo', isDemo: true },
      reach: { data: dailyReach, isReal: false, source: 'Demo', isDemo: true },
      balance: balance,
      googleBalance: googleBalance, // Saldo Google Ads (quando disponível)
      source: 'Demo',
      isDemo: true
    };
  }
  // ==================== DADOS CRM/KOMMO MOCK ====================

  /**
   * Retorna dados CRM/Kommo mock para um cliente
   * Formato compatível com renderKommoLeadsSection()
   */
  getDemoKommoLeadsData(clientId) {
    const config = this._getClientMetricsConfig(clientId);
    const totalAdsLeads = ((config.dailyLeads?.meta || 0) + (config.dailyLeads?.google || 0)) * 30;
    const crmLeads = Math.round(totalAdsLeads * 0.85); // 85% chegam no CRM
    const wonRate = clientId === 'demo_client_001' ? 0.32 : clientId === 'demo_client_005' ? 0.28 : 0.25;
    const wonLeads = Math.round(crmLeads * wonRate);
    const lostLeads = Math.round(crmLeads * 0.22);
    const openLeads = crmLeads - wonLeads - lostLeads;
    const avgTicket = clientId === 'demo_client_001' ? 850 : clientId === 'demo_client_005' ? 497 : 350;
    const totalRevenue = wonLeads * avgTicket;
    const totalSpend = ((config.dailySpend?.meta || 0) + (config.dailySpend?.google || 0)) * 30;

    // Stages do pipeline
    const stages = [
      { id: 'stage_1', name: 'Novo Lead', leadsCount: Math.round(openLeads * 0.35), leadsValue: Math.round(openLeads * 0.35 * avgTicket * 0.3) },
      { id: 'stage_2', name: 'Qualificado', leadsCount: Math.round(openLeads * 0.25), leadsValue: Math.round(openLeads * 0.25 * avgTicket * 0.5) },
      { id: 'stage_3', name: 'Reunião Agendada', leadsCount: Math.round(openLeads * 0.15), leadsValue: Math.round(openLeads * 0.15 * avgTicket * 0.6) },
      { id: 'stage_4', name: 'Reunião Realizada', leadsCount: Math.round(openLeads * 0.12), leadsValue: Math.round(openLeads * 0.12 * avgTicket * 0.7) },
      { id: 'stage_5', name: 'Negociação', leadsCount: Math.round(openLeads * 0.08), leadsValue: Math.round(openLeads * 0.08 * avgTicket * 0.8) },
      { id: 'stage_6', name: 'Proposta', leadsCount: Math.round(openLeads * 0.05), leadsValue: Math.round(openLeads * 0.05 * avgTicket * 0.85) },
      { id: 'stage_won', name: 'Ganho', leadsCount: wonLeads, leadsValue: totalRevenue },
      { id: 'stage_lost', name: 'Perdido', leadsCount: lostLeads, leadsValue: Math.round(lostLeads * avgTicket * 0.4) }
    ];

    // Daily data (30 dias)
    const dailyData = [];
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const variation = 0.7 + Math.random() * 0.6;
      const dayLeads = Math.round((crmLeads / 30) * variation);
      const dayWon = Math.round((wonLeads / 30) * variation);
      dailyData.push({
        date: formatDateISO(d),
        leads: dayLeads,
        won: dayWon
      });
    }

    // Vendedores
    const salespeople = [
      { id: 1001, name: 'Ana Silva', leadsCount: Math.round(crmLeads * 0.35), wonLeads: Math.round(wonLeads * 0.38), revenue: Math.round(totalRevenue * 0.38), conversionRate: 34, avgTicket: Math.round(avgTicket * 1.05) },
      { id: 1002, name: 'Carlos Santos', leadsCount: Math.round(crmLeads * 0.30), wonLeads: Math.round(wonLeads * 0.32), revenue: Math.round(totalRevenue * 0.32), conversionRate: 28, avgTicket: Math.round(avgTicket * 0.95) },
      { id: 1003, name: 'Maria Oliveira', leadsCount: Math.round(crmLeads * 0.35), wonLeads: Math.round(wonLeads * 0.30), revenue: Math.round(totalRevenue * 0.30), conversionRate: 24, avgTicket: Math.round(avgTicket * 0.90) }
    ];

    // Canais
    const channels = [
      { name: 'Meta Ads', leadsCount: Math.round(crmLeads * 0.55), wonLeads: Math.round(wonLeads * 0.52), revenue: Math.round(totalRevenue * 0.52), conversionRate: 30, avgTicket: Math.round(avgTicket * 0.98) },
      { name: 'Google Ads', leadsCount: Math.round(crmLeads * 0.30), wonLeads: Math.round(wonLeads * 0.33), revenue: Math.round(totalRevenue * 0.33), conversionRate: 27, avgTicket: Math.round(avgTicket * 1.05) },
      { name: 'Orgânico', leadsCount: Math.round(crmLeads * 0.15), wonLeads: Math.round(wonLeads * 0.15), revenue: Math.round(totalRevenue * 0.15), conversionRate: 22, avgTicket: Math.round(avgTicket * 0.90) }
    ];

    // Objeções
    const objections = [
      { reason: 'Preço alto', count: Math.round(lostLeads * 0.30), percentage: 30, totalValue: Math.round(lostLeads * 0.30 * avgTicket) },
      { reason: 'Sem resposta', count: Math.round(lostLeads * 0.25), percentage: 25, totalValue: Math.round(lostLeads * 0.25 * avgTicket) },
      { reason: 'Escolheu concorrente', count: Math.round(lostLeads * 0.18), percentage: 18, totalValue: Math.round(lostLeads * 0.18 * avgTicket) },
      { reason: 'Prazo inadequado', count: Math.round(lostLeads * 0.12), percentage: 12, totalValue: Math.round(lostLeads * 0.12 * avgTicket) },
      { reason: 'Sem necessidade atual', count: Math.round(lostLeads * 0.10), percentage: 10, totalValue: Math.round(lostLeads * 0.10 * avgTicket) },
      { reason: 'Outros', count: Math.round(lostLeads * 0.05), percentage: 5, totalValue: Math.round(lostLeads * 0.05 * avgTicket) }
    ];

    // Campaign Attribution (UTM-based)
    const campaignAttribution = [
      { name: 'Campanha Conversão Vendas', source: 'Meta Ads', leadsCount: Math.round(crmLeads * 0.40), wonLeads: Math.round(wonLeads * 0.42), revenue: Math.round(totalRevenue * 0.42) },
      { name: 'Campanha Leads Google', source: 'Google Ads', leadsCount: Math.round(crmLeads * 0.25), wonLeads: Math.round(wonLeads * 0.28), revenue: Math.round(totalRevenue * 0.28) },
      { name: 'Remarketing', source: 'Meta Ads', leadsCount: Math.round(crmLeads * 0.12), wonLeads: Math.round(wonLeads * 0.15), revenue: Math.round(totalRevenue * 0.15) },
    ];

    const adSetAttribution = [
      { name: 'Lookalike 1%', source: 'Meta Ads', campaign: 'Campanha Conversão Vendas', leadsCount: Math.round(crmLeads * 0.22), wonLeads: Math.round(wonLeads * 0.24), revenue: Math.round(totalRevenue * 0.24) },
      { name: 'Interesse Saúde', source: 'Meta Ads', campaign: 'Campanha Conversão Vendas', leadsCount: Math.round(crmLeads * 0.18), wonLeads: Math.round(wonLeads * 0.18), revenue: Math.round(totalRevenue * 0.18) },
      { name: 'Keywords Exatas', source: 'Google Ads', campaign: 'Campanha Leads Google', leadsCount: Math.round(crmLeads * 0.15), wonLeads: Math.round(wonLeads * 0.16), revenue: Math.round(totalRevenue * 0.16) },
    ];

    // Response time metrics (mock)
    const responseMetrics = {
      avgResponseTime: clientId === 'demo_client_001' ? 1800 : clientId === 'demo_client_005' ? 3600 : 2700,
      medianResponseTime: clientId === 'demo_client_001' ? 900 : clientId === 'demo_client_005' ? 2400 : 1500,
      totalWithResponse: Math.round(crmLeads * 0.7),
      totalLeads: crmLeads,
      distribution: { under5min: 35, under15min: 55, under1h: 78, under24h: 95 },
      bySalesperson: {}
    };
    for (const sp of salespeople) {
      const base = sp.id === 1001 ? 600 : sp.id === 1002 ? 1200 : 2100;
      responseMetrics.bySalesperson[sp.id] = { avg: base + 300, median: base, count: Math.round(sp.leadsCount * 0.7) };
    }

    return {
      metrics: { totalLeads: crmLeads, wonLeads, lostLeads, openLeads, totalRevenue },
      stages,
      dailyData,
      salespeople,
      channels,
      objections,
      campaignAttribution,
      adSetAttribution,
      responseMetrics,
      totalSpend
    };
  }

  // ==================== DADOS ANUAIS MOCK ====================

  /**
   * Retorna dados mensais anuais para gráficos de Performance Anual
   * Formato compatível com renderAnnualChartsFromMonthlyData()
   */
  getDemoAnnualMonthlyData(clientId) {
    const config = this._getClientMetricsConfig(clientId);
    const now = new Date();
    const months = [];
    const monthlyData = {};

    // Variação sazonal por mês (índice 0=jan)
    const seasonality = [0.85, 0.88, 0.95, 0.92, 1.0, 0.98, 0.90, 0.93, 1.05, 1.08, 1.15, 1.10];
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

    // Trend de otimização: campanhas melhoram ~2-3% por mês ao longo do ano
    // Meses mais antigos (i=11) → menor eficiência; mês atual (i=0) → melhor
    const optimizationTrend = (monthIndex) => 0.82 + (11 - monthIndex) * 0.018; // 0.82 → 1.0

    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const name = `${monthNames[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`;
      const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
      const s = seasonality[d.getMonth()];
      // Meses futuros: parcial
      const isFuture = d.getFullYear() > now.getFullYear() || (d.getFullYear() === now.getFullYear() && d.getMonth() > now.getMonth());
      const isCurrent = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      const effectiveDays = isFuture ? 0 : isCurrent ? now.getDate() : daysInMonth;
      if (effectiveDays === 0) continue;

      const opt = optimizationTrend(i);

      // Variances INDEPENDENTES por tipo de métrica (simula realidade)
      const spendVar = 0.88 + Math.random() * 0.24;     // ±12% (spend é mais estável)
      const leadsVar = 0.70 + Math.random() * 0.60;     // ±30% (leads flutuam mais)
      const msgsVar = 0.65 + Math.random() * 0.70;      // ±35% (mensagens flutuam bastante)
      const salesVar = 0.60 + Math.random() * 0.80;     // ±40% (vendas alta variação)
      const revenueVar = 0.55 + Math.random() * 0.90;   // ±45% (receita varia com ticket)

      const metaDailyMsgs = typeof config.dailyMessages === 'object' ? config.dailyMessages.meta : config.dailyMessages || 0;
      const googleDailyMsgs = typeof config.dailyMessages === 'object' ? config.dailyMessages.google : 0;

      months.push({ key, name });
      monthlyData[key] = {
        metaSpend: Math.round((config.dailySpend?.meta || 0) * effectiveDays * s * spendVar),
        googleSpend: Math.round((config.dailySpend?.google || 0) * effectiveDays * s * spendVar),
        metaLeads: Math.round((config.dailyLeads?.meta || 0) * effectiveDays * s * leadsVar * opt),
        googleLeads: Math.round((config.dailyLeads?.google || 0) * effectiveDays * s * leadsVar * opt),
        metaMessages: Math.round(metaDailyMsgs * effectiveDays * s * msgsVar * opt),
        googleMessages: Math.round(googleDailyMsgs * effectiveDays * s * msgsVar * opt),
        metaPurchases: Math.round((config.dailySales?.meta || 0) * effectiveDays * s * salesVar * opt),
        googlePurchases: Math.round((config.dailySales?.google || 0) * effectiveDays * s * salesVar * opt),
        metaRevenue: Math.round((config.dailyRevenue?.meta || 0) * effectiveDays * s * revenueVar * opt),
        googleRevenue: Math.round((config.dailyRevenue?.google || 0) * effectiveDays * s * revenueVar * opt)
      };
    }

    return { monthlyData, months, lastUpdate: Date.now() };
  }
}

// Exportar instância singleton
export const demoModeService = new DemoModeService();

// Expor globalmente para debug
window.demoMode = {
  enable: () => demoModeService.enable(),
  disable: () => demoModeService.disable(),
  toggle: () => demoModeService.toggle(),
  status: () => demoModeService.getStatus(),
  getAgency: () => demoModeService.getDemoAgency(),
  getClients: () => demoModeService.getDemoClients(),
  getDashboard: (period) => demoModeService.getDemoDashboardSummary(period)
};
